
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import {
  ClearRounded,
  EditRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import {
  Box,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { connect } from "react-redux";

import { Alert, Dialog, Table } from "../../../layout";
import AddEditBills from "./AddEditBills";

import * as actions from "../../../api/actions/pos";
import { controller, endpoints } from "../../../api/actions/api";
import { AppContext } from "../../../App";
import { safeOnComplete } from "../../../utils/safeOnComplete";
import { formatCurrency } from "../../../hooks/Currency";

import ReportPrintButton from "../../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Expense Details
//------------------------------------------------
const ExpenseDetailsContent = ({ expense, store, t }) => {
  const displayAmount =
    expense?.amount === null ||
    expense?.amount === undefined ||
    expense?.amount === ""
      ? "-"
      : formatCurrency(Number(expense.amount), store);

  const fields = [
    {
      label: t("expenseName"),
      value: expense?.payType,
    },
    {
      label: t("store"),
      value: expense?.store,
    },
    {
      label: t("total"),
      value: displayAmount,
    },
    {
      label: t("note"),
      value: expense?.note,
    },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("expenseDetails")}
      </Typography>

      {fields.map((field, index) => (
        <React.Fragment key={index}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                minWidth: "35%",
              }}
            >
              {field.label}:
            </Typography>

            <Typography
              sx={{
                textAlign: "right",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
              }}
            >
              {field.value === null ||
              field.value === undefined ||
              field.value === ""
                ? "-"
                : field.value}
            </Typography>
          </Box>

          <Divider />
        </React.Fragment>
      ))}
    </Box>
  );
};

//------------------------------------------------
// PayBills - Expenses
//------------------------------------------------
const PayBills = (props) => {
  const { t } = useTranslation();

  const {
    _list,
    _getById,
    _clear,
    _type,
    _delete,
    _store,
  } = props;

  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);
  const detailsRef = useRef(null);

  const [showForm, setShowForm] = useState(false);
  const [updData, setUpdData] = useState();

  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Expenses
  //------------------------------------------------
  useEffect(() => {
    if (_store?.id) {
      setLoader(true);

      _getById(
        endpoints.Expense + controller.GetByStoreId,
        _store.id,
        actions.ActionType.List,
        () => setLoader(false)
      );
    }

    return () => {
      _clear();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  //------------------------------------------------
  // Add / Edit
  //------------------------------------------------
  const handleAddEdit = (val) => {
    setUpdData(val);
    setShowForm(true);
  };

  //------------------------------------------------
  // Expense Table Data
  //------------------------------------------------
  const tableData = useMemo(() => {
    const validTypes = [
      "expenses",
      "postExpense",
      "putExpense",
      "deleteExpense",
    ];

    if (
      !validTypes.includes(_type) ||
      !Array.isArray(_list)
    ) {
      return [];
    }

    return _list.map((obj) => ({
      ...obj,
      payType: obj.expenseCat?.name || "",
      store: obj.store?.storeName || "",
    }));
  }, [_list, _type]);

  //------------------------------------------------
  // View Expense
  //------------------------------------------------
  const handleView = (data) => {
    const expense = tableData.find(
      (item) => Number(item.id) === Number(data.id)
    );

    setViewData(expense || data);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setViewData(null);
  };

  //------------------------------------------------
  // Delete Expense
  //------------------------------------------------
  const handleDlt = (id) => {
    setAlert({
      isOpen: true,
      type: "confirmation",
      title: t("areYouSure"),
      subTitle: t("deleteRecordConfirmation"),

      onConfirm: () => {
        const onComplete = safeOnComplete(
          (res) => {
            setAlert({
              isOpen: true,
              type:
                res.status === 200
                  ? "success"
                  : "warning",
              title:
                res.status === 200
                  ? t("deletedSuccessfully")
                  : t("warning"),
              subTitle: res.msg,
            });
          },
          (err) => {
            setAlert({
              isOpen: true,
              type: "error",
              title: t("error"),
              subTitle: err.msg,
            });
          }
        );

        _delete(
          endpoints.Expense,
          id,
          actions.ActionType.RmvList,
          onComplete
        );
      },
    });
  };

  //------------------------------------------------
  // Table Columns
  //------------------------------------------------
  const screenColumns = [
    { id: "actions", label: t("actions") },
    { id: "payType", label: t("expenseName") },
    { id: "store", label: t("store") },
    { id: "amount", label: t("total") },
    { id: "note", label: t("note") },
  ];

  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  //------------------------------------------------
  // Add Expense Button
  //------------------------------------------------
  const HeaderComp = () => (
    <Button
      onClick={() => handleAddEdit()}
      className="btn-secondary"
      sx={{
        width: { md: "180px", xs: "100%" },
        mb: { xs: "10px", md: "0px" },
        fontSize: "17px",
        padding: "8px",
      }}
    >
      {t("addPayBill")}
    </Button>
  );

  //------------------------------------------------
  // Action Buttons
  //------------------------------------------------
  const ActionComp = (data) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {/* View */}
      <Tooltip arrow title={t("view")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Edit */}
      <Tooltip arrow title={t("edit")}>
        <IconButton
          onClick={() => handleAddEdit(data)}
          size="small"
          className="btn-primary"
        >
          <EditRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>

      {/* Delete */}
      <Tooltip arrow title={t("delete")}>
        <IconButton
          onClick={() => handleDlt(data.id)}
          size="small"
          className="btn-error"
        >
          <ClearRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      {/* Header and Print Preview */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("expenses")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("expenses")}
        />
      </Box>

      {/* Normal Screen Table */}
      <Table
        ActionComp={ActionComp}
        HeaderComp={HeaderComp}
        tblHead={screenColumns}
        tblData={tableData}
      />

      {/* Printable Expenses List */}
      <Box
        ref={reportRef}
        className="pay-bills-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            mb: 2,
          }}
        >
          {t("expenses")}
        </Typography>

        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={tableData}
        />
      </Box>

      {/* Alert */}
      <Alert
        alert={alert}
        setAlert={setAlert}
      />

      {/* Add / Edit Expense Dialog */}
      {showForm && (
        <Dialog show={showForm} onClose={false}>
          <AddEditBills
            setShowForm={setShowForm}
            updData={updData}
          />
        </Dialog>
      )}

      {/* View Expense Dialog */}
      {showView && viewData && (
        <Dialog
          show={showView}
          onClose={handleCloseView}
          maxWidth="sm"
        >
          <Box sx={{ p: 3 }}>
            {/* Print Preview */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <ReportPrintButton
                reportRef={detailsRef}
                paperSize="Letter"
                reportTitle={t("expenseDetails")}
              />
            </Box>

            {/* Screen Details */}
            <Box
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <ExpenseDetailsContent
                expense={viewData}
                store={_store}
                t={t}
              />
            </Box>

            {/* Printable Details */}
            <Box
              ref={detailsRef}
              className="pay-bill-details-print"
              sx={{
                position: "absolute",
                left: "-10000px",
                top: 0,
                width: "1000px",
                backgroundColor: "#fff",
                color: "#000",
                p: 2,
              }}
            >
              <ExpenseDetailsContent
                expense={viewData}
                store={_store}
                t={t}
              />
            </Box>

            {/* Close */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                onClick={handleCloseView}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _list: state.pos.list,
  _type: state.pos.type,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
  _delete: actions.Delete,
  _clear: actions.clear,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(PayBills);
